import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Alert,
  Tooltip
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { Icon } from "@iconify/react";
import { useListWithCompanyId, useMutate } from "src/hooks/useFetch.hook";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";
import { useParams } from "react-router-dom";
import { BaseTable } from "../BaseTable/BaseTable";
import { withoutLicensetableColumns } from "./teams.constants";
import { chargerLicenseUrl } from "src/react-query/endPoints";

const AddLicenseModal = ({ open, onClose, onLicenseAdded }) => {
  const { id: teamId } = useParams();
  const dispatch = useDispatch();
  const [chargersWithoutLicense, setChargersWithoutLicense] = useState([]);
  const [addingLicenseFor, setAddingLicenseFor] = useState({});
  
  const {
    actions: { setAlert },
  } = alertSlice;

  const { 
    data: chargers, 
    isFetching: isChargersFetching,
    refetch: refetchChargers
  } = useListWithCompanyId(
    "chargersWithoutLicense",
    open && !!teamId,
    teamId
  );

  const { mutate: addLicense } = useMutate("addChargerLicense");

  // Update local state when data is fetched
  useEffect(() => {
    if (chargers && Array.isArray(chargers)) {
      setChargersWithoutLicense(chargers);
    } else if (chargers) {
      setChargersWithoutLicense([]);
    }
  }, [chargers]);

  const handleAddLicense = useCallback((charger) => {
    const chargerId = charger?.chargebox_id;
    
    setAddingLicenseFor(prev => ({ ...prev, [chargerId]: true }));
    
    addLicense({
      url: chargerLicenseUrl(teamId),
      method: "POST",
      body: { chargebox_id: chargerId }
    }, {
      onSuccess: () => {
        // Remove the charger from local list
        setChargersWithoutLicense(prev => 
          prev.filter(c => c.chargebox_id !== chargerId)
        );
        
        // Call the refresh function following existing pattern
        if (onLicenseAdded) {
          onLicenseAdded();
        }
        
        dispatch(
          setAlert({
            open: true,
            message: `License added successfully for charger ${charger.chargebox_id}`,
            severity: enums.SEVERITY.SUCCESS,
          })
        );
        
        setAddingLicenseFor(prev => ({ ...prev, [chargerId]: false }));
      },
      onError: (error) => {
        dispatch(
          setAlert({
            open: true,
            message: `Failed to add license for charger ${charger.chargebox_id}: ${error.message}`,
            severity: enums.SEVERITY.ERROR,
          })
        );
        
        setAddingLicenseFor(prev => ({ ...prev, [chargerId]: false }));
      }
    });
  }, [addLicense, teamId, dispatch, setAlert]);

  const handleRefresh = useCallback(() => {
    refetchChargers();
  }, [refetchChargers]);

  const handleClose = useCallback(() => {
    setChargersWithoutLicense([]);
    setAddingLicenseFor({});
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing on backdrop click
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "400px",
          maxHeight: "80vh"
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add License to Chargers</Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh charger list">
              <IconButton
                onClick={handleRefresh}
                disabled={isChargersFetching}
                size="small"
              >
                <Icon icon="eva:refresh-fill" />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={handleClose}
              size="small"
            >
              <GridCloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {isChargersFetching ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {chargersWithoutLicense.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No chargers found without licenses. All chargers already have licenses assigned.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {chargersWithoutLicense.length} {`${chargersWithoutLicense.length === 1 ? 'charger' : 'chargers'}`} available for license assignment
                </Typography>
                
                <BaseTable
                  rows={chargersWithoutLicense}
                  columns={withoutLicensetableColumns(handleAddLicense, addingLicenseFor)}
                  getRowId={(row) => row.chargebox_id || row.id}
                  loading={isChargersFetching}
                  pageSize={10}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddLicenseModal;